"use client";

import { cn } from "@/lib/utils";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: "primary" | "secondary" | "ghost";
    size?: "default" | "lg";
    href?: string;
    external?: boolean;
    children: React.ReactNode;
}

export function Button({
    variant = "primary",
    size = "default",
    href,
    external,
    children,
    className,
    ...props
}: ButtonProps) {
    const baseStyles =
        "relative inline-flex items-center justify-center font-medium tracking-wide transition-all duration-300 rounded-lg cursor-pointer select-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mars";

    const variants = {
        primary:
            "bg-mars text-white hover:bg-mars-light shadow-lg shadow-mars-glow hover:shadow-xl hover:shadow-mars-glow active:scale-[0.98]",
        secondary:
            "border border-border-light text-text hover:border-mars hover:text-mars-light bg-transparent active:scale-[0.98]",
        ghost:
            "text-text-secondary hover:text-text bg-transparent underline-offset-4 hover:underline",
    };

    const sizes = {
        default: "px-6 py-3 text-sm",
        lg: "px-8 py-4 text-base",
    };

    const classes = cn(baseStyles, variants[variant], sizes[size], className);

    if (href) {
        return (
            <a
                href={href}
                className={classes}
                {...(external
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : {})}
            >
                {children}
            </a>
        );
    }

    return (
        <button className={classes} {...props}>
            {children}
        </button>
    );
}
